import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { openWhatsapp, sanitiseIndianWhatsappNumber } from "../utils/waOpener";
import { WhatsappLogo, X, Eye, PaperPlaneTilt, Link as LinkIcon } from "@phosphor-icons/react";

/**
 * Reusable WhatsApp Send modal — Phase 4b-2.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - module: string (lead | quotation | invoice | collection | purchase | vendor | delivery | service | amc | hr)
 *  - receiver: { name, phone, type ('customer'|'vendor'|'staff'), id?, address?, email? }
 *  - context: { variables: { name, doc_number, amount, expiry_date, ... }, related_doc?: { type, id, number } }
 *  - user: current logged-in user (sender)
 *  - defaultAction?: optional action key to preselect template
 *  - onSent?: (logId) => void
 */
const WhatsAppSendModal = ({ open, onClose, module, receiver, context = {}, user, defaultAction = "", onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfLink, setPdfLink] = useState("");
  const [companySettings, setCompanySettings] = useState({});

  // Load templates filtered by module + receiver type
  useEffect(() => {
    if (!open || !module) return;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ module });
        if (receiver?.type) params.set("receiver_type", receiver.type);
        const [tplRes, csRes] = await Promise.all([
          axios.get(`${API}/whatsapp/templates?${params.toString()}`),
          axios.get(`${API}/company-settings`).catch(() => ({ data: {} })),
        ]);
        setTemplates(tplRes.data || []);
        setCompanySettings(csRes.data || {});
        // Pre-select default action or first template
        const pick = (tplRes.data || []).find(t => t.action === defaultAction) || (tplRes.data || [])[0];
        if (pick) {
          setTplId(pick.template_id);
        }
      } catch (e) {
        toast.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, module, receiver?.type, defaultAction]);

  // Load PDF link if related doc provided and template references {*_pdf_link}
  const relatedDoc = context?.related_doc || null;
  useEffect(() => {
    if (!open || !relatedDoc?.type || !relatedDoc?.id) { setPdfLink(""); return; }
    (async () => {
      try {
        const r = await axios.post(
          `${API}/whatsapp/public-link?requester_role=${user?.role || ""}&requester_user_id=${user?.user_id || ""}&requester_name=${encodeURIComponent(user?.name || "")}`,
          { doc_type: relatedDoc.type, doc_id: relatedDoc.id }
        );
        // Use full_url if it points to a real domain; else compose with current origin
        const fu = r.data?.full_url || "";
        const isAbsolute = /^https?:\/\//.test(fu) && !fu.includes("swiftechindia.com/public") || fu.startsWith(window.location.origin);
        setPdfLink(isAbsolute ? fu : `${window.location.origin}${r.data?.public_url || ""}`);
      } catch (e) {
        // If we cannot mint a link (doc missing etc.), keep empty so template variable stays as placeholder
        setPdfLink("");
      }
    })();
  }, [open, relatedDoc?.type, relatedDoc?.id, user?.role, user?.user_id, user?.name]);

  // Build full variable map
  const variables = useMemo(() => {
    const v = {
      name: receiver?.name || "",
      sender_name: user?.name || "",
      sender_phone: user?.whatsapp_number || user?.phone || "",
      company_name: companySettings.company_name || "SwifTech India",
      company_phone: companySettings.phone || "",
      company_email: companySettings.email || "",
      ...(context?.variables || {}),
    };
    // Inject the right *_pdf_link based on related doc type
    if (pdfLink && relatedDoc?.type) {
      const map = {
        "quotation": "quotation_pdf_link",
        "invoice": "invoice_pdf_link",
        "receipt": "receipt_pdf_link",
        "purchase-order": "po_pdf_link",
        "delivery-challan": "delivery_challan_pdf_link",
        "installation-completion": "installation_completion_pdf_link",
        "customer-statement": "customer_statement_pdf_link",
      };
      const key = map[relatedDoc.type];
      if (key) v[key] = pdfLink;
    }
    return v;
  }, [receiver, user, companySettings, context, pdfLink, relatedDoc?.type]);

  // Render template body with variables
  useEffect(() => {
    const tpl = templates.find(t => t.template_id === tplId);
    if (!tpl) { setBody(""); return; }
    let out = tpl.body || "";
    Object.keys(variables).forEach(k => {
      const re = new RegExp(`\\{${k}\\}`, "g");
      out = out.replace(re, variables[k] ?? "");
    });
    setBody(out);
  }, [tplId, templates, variables]);

  const sanitisedReceiver = sanitiseIndianWhatsappNumber(receiver?.phone || "");

  const handleSend = async () => {
    if (!sanitisedReceiver) {
      toast.error("Receiver WhatsApp number missing or invalid (need 91XXXXXXXXXX)");
      return;
    }
    if (!body.trim()) {
      toast.error("Message body is empty");
      return;
    }
    setBusy(true);
    const result = await openWhatsapp(sanitisedReceiver, body);
    // Always log even if popup blocked — user might still click Reopen
    try {
      const tpl = templates.find(t => t.template_id === tplId);
      const r = await axios.post(`${API}/whatsapp/manual-log`, {
        receiver_name: receiver?.name || "",
        receiver_phone: sanitisedReceiver,
        receiver_type: receiver?.type || "customer",
        sender_user_id: user?.user_id || "",
        sender_name: user?.name || "",
        sender_role: user?.role || "",
        sender_department: user?.department || "",
        module,
        action: tpl?.action || "",
        template_id: tpl?.template_id || "",
        template_name: tpl?.name || "",
        message: body,
        pdf_link: pdfLink || "",
        related_doc_type: relatedDoc?.type || "",
        related_doc_id: relatedDoc?.id || "",
        related_doc_number: relatedDoc?.number || "",
        sending_method: "click_to_send",
      });
      onSent?.(r.data?.wa_log_id);
      toast.success(result.status === "popup_blocked" ? "Popup blocked — use the Reopen button" : "WhatsApp opened — log saved");
    } catch (e) {
      toast.error("Log failed (WhatsApp may still have opened)");
    } finally {
      setBusy(false);
    }
    if (result.status !== "popup_blocked") {
      onClose?.();
    }
  };

  if (!open) return null;

  const tpl = templates.find(t => t.template_id === tplId);

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="wa-send-modal">
      <div className="bg-white w-full md:max-w-2xl border border-slate-200 shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-emerald-50">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={20} weight="fill" className="text-emerald-600" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">{module} · {receiver?.type || "customer"}</div>
              <div className="text-base font-semibold text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>Send WhatsApp · {receiver?.name || "—"}</div>
            </div>
          </div>
          <button data-testid="wa-modal-close" onClick={onClose} className="p-2 hover:bg-emerald-100 transition-colors"><X size={16} /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {/* Receiver info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Receiver</div>
              <div className="font-semibold text-slate-900">{receiver?.name || "—"}</div>
              <div data-testid="wa-receiver-phone" className="font-mono text-emerald-700 mt-0.5">
                {sanitisedReceiver ? `+${sanitisedReceiver}` : <span className="text-rose-600">No valid phone</span>}
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sender</div>
              <div className="font-semibold text-slate-900">{user?.name || "—"}</div>
              <div className="text-slate-500 capitalize">{user?.role || "—"}{user?.department ? ` · ${user.department}` : ""}</div>
            </div>
          </div>

          {/* Template picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Template</label>
            {loading ? (
              <div className="text-xs text-slate-500">Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">No templates yet for this module / receiver type.</div>
            ) : (
              <select
                data-testid="wa-template-select"
                value={tplId}
                onChange={e => setTplId(e.target.value)}
                className="w-full rounded-none border border-slate-300 focus:border-emerald-600 p-2 text-sm outline-none"
              >
                {templates.map(t => (
                  <option key={t.template_id} value={t.template_id}>{t.name} ({t.action})</option>
                ))}
              </select>
            )}
            {tpl?.is_system && <div className="text-[10px] text-slate-500 mt-1">System template — admin can edit globally in Admin → WhatsApp Templates.</div>}
          </div>

          {/* PDF link injection notice */}
          {relatedDoc?.type && (
            <div data-testid="wa-pdf-link" className="bg-blue-50 border border-blue-200 p-3 text-xs">
              <div className="flex items-center gap-2 mb-1">
                <LinkIcon size={14} weight="bold" className="text-blue-700" />
                <span className="font-bold text-blue-700 uppercase tracking-wider text-[10px]">Public PDF link auto-injected</span>
              </div>
              <div className="font-mono break-all text-blue-900">{pdfLink || "Generating…"}</div>
              <div className="text-blue-700 mt-1">Link is securely tokenised. Owner gets in-app notification when opened.</div>
            </div>
          )}

          {/* Editable message body */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Message · Editable</label>
            <textarea
              data-testid="wa-message-body"
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              className="w-full rounded-none border border-slate-300 focus:border-emerald-600 p-3 text-sm outline-none font-mono leading-relaxed"
              placeholder="Type your message…"
            />
            <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
              <Eye size={12} /> {body.length} chars · variables auto-rendered
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 bg-slate-50">
          <button data-testid="wa-cancel-btn" onClick={onClose} className="rounded-none border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-100">Cancel</button>
          <button
            data-testid="wa-send-btn"
            disabled={busy || !sanitisedReceiver || !body.trim()}
            onClick={handleSend}
            className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-2 text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Opening…" : "Open WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSendModal;
